import React, { createContext, useContext, useRef, useCallback } from 'react';
import { getCityInventory } from '../services/cityApi';
import { debugLog } from '../utils/environment';
import {
    EQUIPPED_STORE_TTL_MS,
    readStoredEquipped,
    removeStoredEquipped,
    writeStoredEquipped
} from '../utils/equippedCityStore';

const CityEquippedCacheContext = createContext(null);

export const useCityEquippedCache = () => {
    const context = useContext(CityEquippedCacheContext);
    if (!context) {
        throw new Error('useCityEquippedCache must be used within CityEquippedCacheProvider');
    }
    return context;
};

// 캐시 유효 시간: 15분 (장착 변경 시 invalidateCache()로 즉시 갱신)
// 내 계정에만 적용한다. 남의 장착 CITY는 기기에 24시간 저장해 앱을 다시 열어도 서버를 부르지 않는다.
const CACHE_TTL_MS = 15 * 60 * 1000;

export const CityEquippedCacheProvider = ({ children, environment, selfUserId }) => {
    // 메모리 캐시: userId -> { data: any, timestamp: number }
    const cacheRef = useRef(new Map());

    // 중복 요청 방지: userId -> Promise
    const pendingRequestsRef = useRef(new Map());

    /**
     * 특정 유저의 장착 도시 정보를 가져옵니다. (캐싱 적용)
     * @param {string} userId 
     * @param {boolean} forceRefresh 
     */
    const getCachedCityEquipped = useCallback(async (userId, forceRefresh = false) => {
        if (!userId) return null;

        const now = Date.now();
        const cache = cacheRef.current;
        // 💰 [비용 최적화] 남의 장착 CITY는 거의 안 바뀌므로 24시간 저장 (내 계정은 15분 유지)
        const persistent = userId !== selfUserId;
        const ttl = persistent ? EQUIPPED_STORE_TTL_MS : CACHE_TTL_MS;

        // 1. 캐시 확인
        if (!forceRefresh && cache.has(userId)) {
            const entry = cache.get(userId);
            if (now - entry.timestamp < ttl) {
                // 유효한 캐시가 있으면 즉시 반환
                return entry.data;
            }
        }

        // 1-2. 기기에 저장된 값 확인 (앱을 다시 열어도 서버 호출 없이 사용)
        if (!forceRefresh && persistent) {
            const stored = readStoredEquipped(environment, userId, { now });
            if (stored) {
                cache.set(userId, stored);
                return stored.data;
            }
        }

        // 2. 진행 중인 요청 확인 (Deduping)
        if (pendingRequestsRef.current.has(userId)) {
            return pendingRequestsRef.current.get(userId);
        }

        // 3. API 호출
        const promise = (async () => {
            try {
                // debugLog(`[CityCache] Fetching for ${userId}`);
                const data = await getCityInventory(userId, environment, { equippedOnly: true });
                let equippedData = [];
                if (Array.isArray(data.equippedList)) {
                  equippedData = data.equippedList.filter((x) => x != null);
                }
                if (equippedData.length === 0 && data.equipped) {
                  equippedData = [data.equipped];
                }

                // 캐시 업데이트
                cacheRef.current.set(userId, {
                    data: equippedData,
                    timestamp: Date.now()
                });
                if (persistent) writeStoredEquipped(environment, userId, equippedData);

                return equippedData;
            } catch (error) {
                console.warn(`[CityCache] Failed to fetch for ${userId}`, error);
                return []; // 에러 시 빈 배열 반환 (캐시는 하지 않음)
            } finally {
                // 요청 완료 후 pending 맵에서 제거
                pendingRequestsRef.current.delete(userId);
            }
        })();

        pendingRequestsRef.current.set(userId, promise);
        return promise;

    }, [environment, selfUserId]);

    /**
     * 캐시를 수동으로 무효화합니다 (예: 장착 변경 시)
     */
    const invalidateCache = useCallback((userId) => {
        if (userId) {
            cacheRef.current.delete(userId);
            removeStoredEquipped(environment, userId);
            debugLog(`[CityCache] Invalidated for ${userId}`);
        }
    }, [environment]);

    const value = {
        getCachedCityEquipped,
        invalidateCache
    };

    return (
        <CityEquippedCacheContext.Provider value={value}>
            {children}
        </CityEquippedCacheContext.Provider>
    );
};
